<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { OnClickOutside } from '@vueuse/components';
import { useAlert } from 'dashboard/composables';
import ContactAPI from 'dashboard/api/contacts';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ProjectEmptyState from './ProjectEmptyState.vue';
import ProjectRolesCell from './ProjectRolesCell.vue';

const props = defineProps({
  projectId: {
    type: [String, Number],
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();

// Fixed contact-role vocabulary; labels are localized, values match the backend enum.
const ROLE_VALUES = ['sponsor', 'stakeholder', 'approver', 'end_user'];
const roleOptions = computed(() =>
  ROLE_VALUES.map(value => ({
    value,
    label: t(`PROJECTS.STAKEHOLDERS.ROLE.${value.toUpperCase()}`),
  }))
);

const projectContacts = computed(() =>
  store.getters['projectContacts/getContactsByProject'](Number(props.projectId))
);
const uiFlags = computed(() => store.getters['projectContacts/getUIFlags']);

const project = computed(() => store.getters.getProjectById(props.projectId));
const empresaId = computed(() => project.value?.empresa_id || null);
const empresa = computed(() =>
  empresaId.value ? store.getters.getEmpresaById(empresaId.value) : null
);

const pickerOpen = ref(false);
const search = ref('');
const searchResults = ref([]);

const linkedContactIds = computed(
  () => new Set(projectContacts.value.map(pc => pc.contact_id))
);

// Default suggestions: the linked empresa's contacts not already on the project.
const empresaContacts = computed(() =>
  (empresa.value?.contacts || []).filter(c => !linkedContactIds.value.has(c.id))
);

// What the picker shows: empresa contacts by default, account-wide search results when typing.
const pickerContacts = computed(() => {
  if (search.value.trim()) {
    return searchResults.value.filter(c => !linkedContactIds.value.has(c.id));
  }
  return empresaContacts.value;
});

const loadContacts = () =>
  store.dispatch('projectContacts/get', { projectId: props.projectId });

const loadEmpresa = () => {
  if (empresaId.value) store.dispatch('fetchEmpresa', { id: empresaId.value });
};

let searchTimer = null;
const runSearch = async () => {
  if (!search.value.trim()) {
    searchResults.value = [];
    return;
  }
  const { data } = await ContactAPI.search(search.value.trim(), 1);
  searchResults.value = data?.payload || [];
};
const debouncedSearch = () => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(runSearch, 300);
};

const addContact = async contact => {
  try {
    await store.dispatch('projectContacts/create', {
      projectId: props.projectId,
      contactId: contact.id,
      roles: [],
    });
    search.value = '';
    searchResults.value = [];
    pickerOpen.value = false;
  } catch (error) {
    useAlert(t('PROJECTS.STAKEHOLDERS.ERROR'));
  }
};

const updateRoles = async (projectContact, roles) => {
  try {
    await store.dispatch('projectContacts/update', {
      projectId: props.projectId,
      id: projectContact.id,
      roles,
    });
  } catch (error) {
    useAlert(t('PROJECTS.STAKEHOLDERS.ERROR'));
  }
};

const removeContact = async projectContact => {
  try {
    await store.dispatch('projectContacts/delete', {
      projectId: props.projectId,
      id: projectContact.id,
    });
  } catch (error) {
    useAlert(t('PROJECTS.STAKEHOLDERS.ERROR'));
  }
};

watch(() => props.projectId, loadContacts);
watch(empresaId, loadEmpresa);
onMounted(() => {
  loadContacts();
  loadEmpresa();
});
</script>

<template>
  <section class="flex flex-col rounded-lg border border-n-weak bg-n-solid-1">
    <!-- Card header: title + count + add stakeholder -->
    <header
      class="flex items-center gap-2 px-4 h-12 border-b border-n-weak shrink-0"
    >
      <span class="i-lucide-contact size-4 text-n-slate-11" />
      <h3 class="text-sm font-medium text-n-slate-12">
        {{ t('PROJECTS.PEOPLE.STAKEHOLDERS_TITLE') }}
      </h3>
      <span
        class="px-1.5 py-0.5 text-xs rounded-md tabular-nums bg-n-alpha-2 text-n-slate-11"
      >
        {{ projectContacts.length }}
      </span>
      <div class="relative ml-auto">
        <OnClickOutside @trigger="pickerOpen = false">
          <Button
            :label="t('PROJECTS.STAKEHOLDERS.ADD')"
            icon="i-lucide-user-plus"
            size="sm"
            color="blue"
            @click="pickerOpen = !pickerOpen"
          />
          <div
            v-if="pickerOpen"
            class="absolute right-0 z-20 mt-1 w-80 overflow-hidden border rounded-md shadow-lg border-n-weak bg-n-solid-2"
          >
            <input
              v-model="search"
              class="w-full h-8 px-2.5 text-sm border-b reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none"
              :placeholder="t('PROJECTS.STAKEHOLDERS.SEARCH_PLACEHOLDER')"
              @input="debouncedSearch"
            />
            <p
              v-if="!search.trim() && empresa"
              class="px-3 py-1.5 text-xs text-n-slate-10 bg-n-alpha-1"
            >
              {{
                t('PROJECTS.STAKEHOLDERS.FROM_EMPRESA', { name: empresa.name })
              }}
            </p>
            <ul class="overflow-y-auto max-h-64 divide-y divide-n-weak/60">
              <li
                v-for="contact in pickerContacts"
                :key="contact.id"
                class="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-n-alpha-1"
                @click="addContact(contact)"
              >
                <Avatar
                  :name="contact.name"
                  :src="contact.thumbnail"
                  :size="22"
                />
                <span class="font-medium truncate text-n-slate-12">
                  {{ contact.name }}
                </span>
                <span class="truncate text-n-slate-10">
                  {{ contact.email || '' }}
                </span>
              </li>
              <li
                v-if="!pickerContacts.length"
                class="px-3 py-2 text-xs text-n-slate-10"
              >
                {{ t('PROJECTS.STAKEHOLDERS.NO_CONTACTS') }}
              </li>
            </ul>
          </div>
        </OnClickOutside>
      </div>
    </header>

    <!-- Card body: stakeholders list -->
    <div class="p-4">
      <ProjectEmptyState
        v-if="!projectContacts.length && !uiFlags.isFetching"
        icon="i-lucide-contact"
        :title="t('PROJECTS.STAKEHOLDERS.EMPTY')"
      />
      <table v-else class="w-full text-sm border-collapse">
        <thead>
          <tr
            class="text-xs text-left uppercase text-n-slate-10 [&>th]:font-medium [&>th]:pb-2 [&>th]:pr-3"
          >
            <th>{{ t('PROJECTS.STAKEHOLDERS.COL_CONTACT') }}</th>
            <th>{{ t('PROJECTS.STAKEHOLDERS.COL_ROLES') }}</th>
            <th class="w-8" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="projectContact in projectContacts"
            :key="projectContact.id"
            class="group transition-colors [&>td]:border-t [&>td]:border-n-weak/60 [&>td]:py-1.5 [&>td]:pr-3 [&>td]:align-middle hover:bg-n-alpha-1"
          >
            <td class="min-w-0">
              <span class="flex items-center min-w-0 gap-2">
                <Avatar
                  :name="projectContact.contact?.name"
                  :src="projectContact.contact?.thumbnail"
                  :size="24"
                />
                <span class="min-w-0">
                  <span class="block font-medium truncate text-n-slate-12">
                    {{ projectContact.contact?.name }}
                  </span>
                  <span
                    v-if="projectContact.contact?.email"
                    class="block text-xs truncate text-n-slate-10"
                  >
                    {{ projectContact.contact.email }}
                  </span>
                </span>
              </span>
            </td>
            <td>
              <ProjectRolesCell
                :model-value="projectContact.roles || []"
                :options="roleOptions"
                @update:model-value="updateRoles(projectContact, $event)"
              />
            </td>
            <td class="text-right">
              <Button
                v-tooltip.top="t('PROJECTS.STAKEHOLDERS.REMOVE')"
                variant="ghost"
                color="ruby"
                size="xs"
                icon="i-lucide-x"
                class="transition-opacity opacity-0 group-hover:opacity-100"
                @click="removeContact(projectContact)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>
